<template>
  <div class="meeting-page bg-[#F8FAFB]">
    <!-- 상단 네비게이션 바 -->
    <div class="meeting-navbar">
      <span class="aitra-title">AITRA</span>
    </div>

    <!-- 다른 사용자가 발언 중일 때, 상단에 상태를 알려주는 배너입니다. -->
    <div
      v-if="activeSpeaker && user && activeSpeaker.user_id !== user.id"
      class="p-2 text-center bg-[#E8EFF5] text-[#0033A0] text-sm fixed left-0 w-full z-10"
      style="top: 56px"
    >
      {{ translations.isSpeaking(activeSpeaker.user_name) }}
    </div>

    <!-- 메시지 영역 -->
    <div class="messages-area" ref="messagesContainer" :style="messagesAreaStyle">
      <div class="flex-1 py-4 px-1 overflow-y-auto bg-[#F8FAFB]" ref="messagesContainer">
        <div class="space-y-4">
          <div
            v-for="message in messages"
            :key="message.id"
            class="flex"
            :class="{ 'justify-end': message.isOwn, 'justify-start': !message.isOwn }"
          >
            <div
              class="max-w-[80%] p-3 shadow-sm"
              :class="{
                'bg-[#E8EFF5] border border-[#D1E0ED] rounded-2xl rounded-tr-none text-[#0033A0]': message.isOwn,
                'bg-[#0033A0] text-white rounded-2xl rounded-tl-none': !message.isOwn
              }"
            >
              <div
                v-if="!message.isOwn"
                class="mb-1 text-sm font-medium text-[rgba(255,255,255,0.9)]"
              >
                {{ message.sender }}
              </div>
              <div class="mb-1">
                <div
                  class="text-sm"
                  :class="{ 'text-[#0033A0]': message.isOwn, 'text-white': !message.isOwn }"
                >
                  {{ message.translatedText }}
                </div>
              </div>
              <div
                class="text-right text-xs"
                :class="{ 'text-[rgba(0,51,160,0.7)]': message.isOwn, 'text-[rgba(255,255,255,0.7)]': !message.isOwn }"
              >
                {{ message.isOwn ? 'You' : '' }} {{ formatTime(message.timestamp) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 하단 컨트롤 버튼 -->
    <div class="meeting-footer">
      <div class="p-4 flex justify-center items-center gap-20 bg-white border-t border-[#D1E0ED] w-full">
        <button
          @click="showInfoModal = true"
          class="w-14 h-14 bg-[#E8EFF5] text-[#0033A0] rounded-full flex items-center justify-center shadow-md hover:bg-[rgba(0,51,160,0.1)] transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
        </button>

        <button
          @click="handleRecordButtonClick"
          :disabled="isRequestingToSpeak || (activeSpeaker && user && activeSpeaker.user_id !== user.id)"
          class="w-20 h-20 rounded-full flex items-center justify-center transition-colors shadow-lg text-white text-2xl font-bold"
          :class="{
            'bg-gray-400 cursor-not-allowed': isRequestingToSpeak || (activeSpeaker && user && activeSpeaker.user_id !== user.id),
            'bg-[rgba(0,51,160,0.7)]': isRecording,
            'bg-[#0033A0]': !isRecording && !(isRequestingToSpeak || (activeSpeaker && user && activeSpeaker.user_id !== user.id))
          }"
        >
          <!-- 1. 발언 요청 중일 때 (스피너 아이콘) -->
          <svg v-if="isRequestingToSpeak" class="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <!-- 2. 내가 녹음 중일 때 (정지 아이콘) -->
          <svg v-else-if="isRecording" xmlns="http://www.w3.org/2000/svg" class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M6 6h12v12H6z"/></svg>
          <!-- 3. 발언 가능할 때 (마이크 아이콘) -->
          <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
          </svg>
        </button>

        <button
          @click="showExitModal = true"
          class="w-14 h-14 bg-[#E8EFF5] text-[#0033A0] rounded-full flex items-center justify-center shadow-md hover:bg-[rgba(0,51,160,0.1)] transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
        </button>
      </div>
    </div>

    <!-- Meeting Info Modal -->
    <Transition name="modal">
      <MeetingInfoModal
        v-if="showInfoModal"
        :meeting-id="meetingId"
        :is-copied="isCopied"
        :user="user"
        :participants="participants"
        :languages="languages"
        @close="showInfoModal = false"
        @copy="copyMeetingId"
      />
    </Transition>

    <!-- Exit Meeting Modal -->
    <Transition name="modal">
      <ExitMeetingModal
        v-if="showExitModal"
        @close="showExitModal = false"
        @exit="exitMeeting"
      />
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MeetingInfoModal from '../components/modals/MobileMeetingInfoModal.vue'
import ExitMeetingModal from '../components/modals/MobileExitMeetingModal.vue'
import '@/assets/styles/mobile-modal.css'
import fastApiWebSocket from '@/services/fast-api/fastApiWebSocket.js'
import { startTranslation, stopTranslation, cancelTranslation } from '@/services/fast-api/fastApiRESTAPI.js'

const route = useRoute()
const router = useRouter()
const meetingId = ref(route.params.id)
const user = ref(null)
const messages = ref([])
const showInfoModal = ref(false)
const showExitModal = ref(false)
const isCopied = ref(false)
const messagesContainer = ref(null)

const messagesAreaStyle = computed(() => {
  let paddingTop = 56; // 기본 네비게이션 바 높이
  if (activeSpeaker.value && user.value && activeSpeaker.value.user_id !== user.value.id) {
    paddingTop += 36; // 배너 높이 (근사치)
  }
  return {
    paddingTop: `${paddingTop}px`
  };
});

// --- 발언권 관리를 위한 신규 상태 변수 ---
// isRecording은 이제 '내가 발언권을 갖고 녹음 중'인 상태를 의미합니다.
const isRecording = ref(false) 
// 현재 발언자 정보 저장. null이면 발언자 없음.
const activeSpeaker = ref(null); // { id: string, name: string } | null
// 내가 현재 발언권을 요청하고 응답을 기다리는 중인지 여부 (낙관적 UI용)
const isRequestingToSpeak = ref(false);

const translations = computed(() => {
  const lang = user.value?.language;

  switch (lang) {
    case 'Korean':
      return {
        isSpeaking: (name) => `${name} 님이 발화 중입니다.`,
        translatingText: '번역 중입니다.',
      };
    case 'Chinese':
      return {
        isSpeaking: (name) => `${name} 正在发言。`,
        translatingText: '正在翻译...',
      };
    case 'Vietnamese':
      return {
        isSpeaking: (name) => `${name} đang phát biểu.`,
        translatingText: 'Đang dịch...',
      };
    default: // English
      return {
        isSpeaking: (name) => `${name} is speaking.`,
        translatingText: 'Translating...',
      };
  }
});

const languages = [
  { code: "ko", name: "Korean" },
  { code: "en", name: "English" },
  { code: "ja", name: "Japanese" },
  { code: "zh", name: "Chinese" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
]

const participants = ref([])

onMounted(() => {
  const userDataString = localStorage.getItem("meetsync-user");
  if (!userDataString) {
    alert("Please access through the correct path.");
    router.push("/");
    return;
  }
  
  const userData = JSON.parse(userDataString);
  user.value = {
    id: userData.id,
    name: userData.name,
    language: userData.language,
  };
  
  // WebSocket 연결
  fastApiWebSocket.connect(
    userData.meetingId,
    userData.id,
    userData.name,
    userData.language
  );

  // --- 신규 WebSocket 이벤트 리스너 등록 ---
  // 컴포넌트가 마운트될 때, 서버로부터 오는 실시간 이벤트를 수신 대기합니다.
  fastApiWebSocket.addEventListener('speaker_changed', handleSpeakerChanged);
  fastApiWebSocket.addEventListener('speak_request_denied', handleSpeakRequestDenied);
  fastApiWebSocket.addEventListener('subtitle', handleSubtitle);
})

onBeforeUnmount(() => {
  // 2025.06.18 양권상: 컴포넌트 소멸 시 등록했던 WebSocket 이벤트 리스너를 제거하여 메모리 누수를 방지합니다.
  fastApiWebSocket.removeEventListener('speaker_changed', handleSpeakerChanged);
  fastApiWebSocket.removeEventListener('speak_request_denied', handleSpeakRequestDenied);
  fastApiWebSocket.removeEventListener('subtitle', handleSubtitle);

  fastApiWebSocket.disconnect();
  // 컴포넌트가 언마운트될 때 리스너를 명시적으로 제거할 수도 있습니다.
  // 하지만 현재는 서비스가 싱글톤이고 페이지 이동 시 disconnect 하므로 필수적이진 않습니다.
})

// --- 신규 WebSocket 이벤트 핸들러 ---

/**
 * 서버로부터 'speaker_changed' 이벤트를 받았을 때 호출됩니다.
 * 모든 발언권 관련 상태는 이 이벤트를 통해 동기화됩니다.
 * @param {object} data - { active_speaker: { user_id, user_name } | null }
 */
const handleSpeakerChanged = async (data) => {
  // 서버가 보내는 active_speaker 필드를 사용하도록 수정합니다.
  const newSpeaker = data.active_speaker;
  console.log('발언자 변경:', newSpeaker);

  isRequestingToSpeak.value = false; // 발언권이 확정되었으므로 요청 상태는 해제합니다.
  activeSpeaker.value = newSpeaker; // 서버가 알려준 새 발언자로 상태를 업데이트합니다.

  // 서버가 보낸 객체의 user_id와 클라이언트의 id를 비교하도록 수정합니다.
  const amISpeaker = newSpeaker && newSpeaker.user_id === user.value.id;
  
  if (amISpeaker && !isRecording.value) {
    // 내가 새로운 발언자가 되었고, 아직 녹음을 시작하지 않았을 때
    try {
      await startTranslation(); // 실제 마이크 녹음을 시작합니다.
      isRecording.value = true;
      // 사용자에게 시각적 피드백을 주기 위해 "번역 중입니다..." 메시지를 표시합니다.
      messages.value.push({
        id: user.value.id + '_temp' + Date.now(),
        sender: user.value.name,
        translatedText: translations.value.translatingText,
        timestamp: new Date(),
        isOwn: true,
      });
      // 수정: 메시지가 추가될 때마다 스크롤을 맨 아래로 이동시킵니다.
      scrollToBottom();
    } catch (error) {
      alert('마이크 시작에 실패했습니다. 권한을 확인해주세요.');
      // 마이크 시작에 실패하면, 발언권을 즉시 포기하여 다른 사람이 시도할 수 있도록 합니다.
      fastApiWebSocket.send('finished_speaking');
    }
  } else if (!amISpeaker && isRecording.value) {
    // 내가 발언 중이었는데, 서버에 의해 발언권이 해제된 경우 (예: 타임아웃)
    isRecording.value = false;
    cancelTranslation(); // 데이터 전송 없이 진행 중인 녹음을 강제로 취소합니다.
    alert("발언 시간이 초과되었거나, 연결 문제로 발언이 종료되었습니다.");
  }
};

/**
 * 서버로부터 'speak_request_denied' 이벤트를 받았을 때 호출됩니다.
 */
const handleSpeakRequestDenied = () => {
  isRequestingToSpeak.value = false; // 요청 상태를 해제합니다.
  alert('다른 사용자가 발언 중입니다. 잠시 후 다시 시도해주세요.');
};

/**
 * 서버로부터 'subtitle' 이벤트를 받았을 때 호출됩니다. (기존 로직과 동일)
 */
const handleSubtitle = (data) => {
  const isOwnMessage = data.user_id === user.value.id;

  if (isOwnMessage) {
    // 내가 보낸 메시지의 번역 결과가 도착하면 "번역 중입니다..."를 실제 텍스트로 대체합니다.
    const myMessage = messages.value.find(
      (m) => m.isOwn && m.translatedText === translations.value.translatingText
    );
    if (myMessage) {
      myMessage.translatedText = data.text;
      myMessage.id = Date.now().toString() + Math.random();
    }
  } else {
    // 다른 사람의 메시지인 경우 참가자 목록에 추가하고 화면에 표시합니다.
    if (!participants.value.some(p => p.id === data.user_id)) {
        participants.value.push({
            id: data.user_id,
            name: data.speaker_name || 'Unknown',
            language: '',
        });
    }
    messages.value.push({
      id: Date.now().toString() + Math.random(),
      sender: data.speaker_name || 'Unknown',
      translatedText: data.text,
      timestamp: new Date(),
      isOwn: false,
    });
  }
  // 수정: 메시지가 추가될 때마다 스크롤을 맨 아래로 이동시킵니다.
  scrollToBottom();
};

/**
 * 메시지 목록의 스크롤을 맨 아래로 이동시키는 함수입니다.
 */
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const formatTime = (date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const copyMeetingId = async () => {
  try {
    await navigator.clipboard.writeText(meetingId.value)
    isCopied.value = true
    setTimeout(() => isCopied.value = false, 2000)
  } catch (err) {
    alert('Failed to copy Meeting ID.')
  }
}

// --- 핵심 로직 함수 (기존 toggleRecording 대체) ---

/**
 * 중앙의 녹음 버튼을 클릭했을 때 호출되는 메인 핸들러입니다.
 */
const handleRecordButtonClick = () => {
  if (isRecording.value) {
    // 현재 내가 녹음 중이라면 '발언 종료' 로직을 실행합니다.
    handleStopAndUpload();
  } else {
    // 녹음 중이 아니라면 '발언권 요청' 로직을 실행합니다.
    requestToSpeak();
  }
};

/**
 * 서버에 발언권을 요청합니다.
 */
const requestToSpeak = () => {
  // 다른 사람이 발언 중이거나, 내가 이미 요청 중일 때는 중복 요청을 방지합니다.
  if (activeSpeaker.value || isRequestingToSpeak.value) {
    alert("다른 사용자가 발언 중입니다.");
    return;
  }
  isRequestingToSpeak.value = true; // 낙관적 UI: 즉시 UI를 '요청 중' 상태로 변경합니다.
  // BE 요구사항: 서버가 연결 정보로 사용자를 식별하므로 payload 없이 type만 보냅니다.
  fastApiWebSocket.send('request_to_speak');
};

/**
 * 발언을 종료하고 녹음된 오디오를 서버에 업로드합니다.
 * try...finally 구문을 사용하여 안정성을 확보합니다.
 */
const handleStopAndUpload = async () => {
  if (!isRecording.value) return;

  // 해결책: 사용자가 '종료' 버튼을 누르는 즉시, isRecording 상태를 false로 변경합니다.
  // 이렇게 하면 클라이언트가 서버의 '발언자 없음' 신호를 오해하는 것을 방지할 수 있습니다.
  isRecording.value = false;

  try {
    const options = {
      sourceLanguage: user.value.language,
      // 모바일(AITRA) 특성상 자막 옵션을 사용합니다.
      // 변경도 가능합니다.
      // audio or subtitle
      translationOption: 'audio',
      code: meetingId.value,
      userId: user.value.id,
      userName: user.value.name,
    };
    // 녹음을 중지하고 오디오 파일 업로드를 시도합니다.
    await stopTranslation(options);
    console.log('오디오 업로드 성공');
  } catch (error) {
    console.error('오디오 업로드 실패:', error);
    // 실패 시 사용자에게 알립니다.
    alert('음성 전송에 실패했습니다. 메시지가 유실되었을 수 있습니다.');
  } finally {
    // HTTP 요청의 성공/실패 여부와 관계없이,
    // 반드시 서버에 발언 종료를 알려 발언권을 해제합니다.
    // BE 요구사항: 서버가 연결 정보로 사용자를 식별하므로 payload 없이 type만 보냅니다.
    fastApiWebSocket.send('finished_speaking');
  }
};

const exitMeeting = () => {
  router.push("/")
}
</script>
<style>
html, body {
  margin: 0;
  padding: 0;
  background: #F8FAFB;
  height: 100%;
}

.meeting-page {
  background: #F8FAFB;
  min-height: 100vh;
}
.meeting-navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 56px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  z-index: 1000;
  border-bottom: 1px solid #D1E0ED;
}
.navbar-logo {
  width: 32px;
  height: 32px;
  margin-right: 10px;
}
.navbar-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #0033A0;
  margin: 0 auto;
  letter-spacing: 0.08em;
}

.aitra-title {
  font-family: 'Zen Tokyo Zoo', sans-serif;
  font-size: 1.5rem;
  font-weight: bold;
  color: #0033A0;
  letter-spacing: 0.1em;
}
.messages-area {
  height: 100vh;
  overflow-y: auto;
  /* padding-top은 :style 바인딩으로 동적으로 관리됩니다. */
  padding-bottom: 120px;
  box-sizing: border-box;
  background: #F8FAFB; 
} 

.meeting-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background: white;
  border-top: 1px solid #D1E0ED;
}
</style>